import { TypeFeaturesPlansListResponseDto } from '@tiendanube/common';
import { logEvent } from 'commons/utils/tracking';
import {
  BILLING_CHURN_STATUS_CHECK,
  BILLING_GO_TO_ANNUAL_DISCOUNT,
  BILLING_GO_TO_PAY_PLAN_CLICK,
  BILLING_UPSELL_MODAL_CLOSE,
  BILLING_UPSELL_MODAL_SHOW,
  BILLING_UPSELL_PLANS_PAGE_CLICK,
  BILLING_UPSELL_PLAN_CHANGE,
  BILLING_POST_UPSELL_ADBAR_CLICK,
  BILLING_DOWNGRADE_PLAN,
  BILLING_SOFT_BLOCK_PAYMENT_CLICK,
  BILLING_SOFT_BLOCK_MODAL_SHOW,
  TEST_DISCOUNTS_ADBAR_SHOW,
  TEST_DISCOUNTS_ADBAR_CLICK,
  BILLING_ADBAR_DISCOUNT_UPGRADE_CLICK,
  BILLING_UPSELL_DISCOUNT_SHOW,
  BILLING_UPSELL_DISCOUNT_CLOSE,
  BILLING_ADBAR_DISCOUNT_UPGRADE_SHOW,
  BILLING_UPSELL_DISCOUNT_CLICK,
  BILLING_HARD_BLOCK_MODAL_SHOW,
  BILLING_HARD_BLOCK_PAYMENT_CLICK,
  BILLING_CANCEL_SCHEDULED_DOWNGRADE_CLICK,
  BILLING_CANCEL_SCHEDULED_DOWNGRADE_CONFIRM,
  BILLING_CHANGE_PLAN_CLICK,
} from 'config/trackingEvents';
import { ChangeType } from './BillingPlans/pages/BillingPlansPage';

export const trackingBillingGoToPayPlanClick = (
  position: 'top' | 'bottom',
  niceName: string | undefined,
) => {
  logEvent(BILLING_GO_TO_PAY_PLAN_CLICK, {
    position: position,
    nice_name: niceName ?? '',
  });
};

export const trackingBillingChangePlanClick = ({
  position,
  newPlanId,
  currentPlanId,
  changeType,
  scheduledDowngrade,
}: {
  position: 'top' | 'bottom';
  newPlanId: number | undefined;
  currentPlanId: number | undefined;
  changeType: ChangeType;
  scheduledDowngrade: boolean;
}) => {
  logEvent(BILLING_CHANGE_PLAN_CLICK, {
    position: position,
    new_plan_id: newPlanId?.toString() ?? '',
    current_plan_id: currentPlanId?.toString() ?? '',
    change_type: changeType,
    scheduled_downgrade: scheduledDowngrade.toString(),
  });
};

export const trackingBillingDiscountForCancelAccount = (storeId: string) => {
  logEvent(BILLING_GO_TO_ANNUAL_DISCOUNT, {
    store_id: storeId,
  });
};

interface UpsellEventParams {
  featureKey: string;
  currentPlan: string;
  newPlan: string;
  currentPlanValue: TypeFeaturesPlansListResponseDto;
  newPlanValue: TypeFeaturesPlansListResponseDto;
  source?: string;
}

export type ChurnStatus =
  | 'store_active'
  | 'unsynced'
  | 'has_free_plan'
  | 'needs_payment'
  | 'has_remaining_days';

interface ChurnStatusCheckParams {
  status: ChurnStatus;
  route: string;
}

type DowngradeEventParams = Pick<
  UpsellEventParams,
  'currentPlan' | 'newPlan' | 'source'
> & {
  scheduled: boolean;
};

export const trackingBillingUpsellModalShow = ({
  featureKey,
  currentPlan,
  newPlan,
  currentPlanValue,
  newPlanValue,
  source = '',
}: UpsellEventParams) => {
  logEvent(BILLING_UPSELL_MODAL_SHOW, {
    featureKey,
    currentPlan,
    newPlan,
    currentPlanValue: <string>currentPlanValue,
    newPlanValue: <string>newPlanValue,
    source,
  });
};

export const trackingBillingUpsellModalClose = ({
  featureKey,
  currentPlan,
  newPlan,
  currentPlanValue,
  newPlanValue,
  source = '',
}: UpsellEventParams) => {
  logEvent(BILLING_UPSELL_MODAL_CLOSE, {
    featureKey,
    currentPlan,
    newPlan,
    currentPlanValue: <string>currentPlanValue,
    newPlanValue: <string>newPlanValue,
    source,
  });
};

export const trackingBillingUpsellPlanChange = ({
  featureKey,
  currentPlan,
  newPlan,
  currentPlanValue,
  newPlanValue,
  source = '',
}: UpsellEventParams) => {
  logEvent(BILLING_UPSELL_PLAN_CHANGE, {
    featureKey,
    currentPlan,
    newPlan,
    currentPlanValue: <string>currentPlanValue,
    newPlanValue: <string>newPlanValue,
    source,
  });
};

export const trackingBillingDowngradeFreePlan = ({
  currentPlan,
  newPlan,
  source = '',
  scheduled,
}: DowngradeEventParams) => {
  logEvent(BILLING_DOWNGRADE_PLAN, {
    currentPlan,
    newPlan,
    source,
    scheduled: scheduled.toString(),
  });
};

export const trackingBillingUpsellPlansPageClick = ({
  featureKey,
  currentPlan,
  newPlan,
  currentPlanValue,
  newPlanValue,
  source = '',
}: UpsellEventParams) => {
  logEvent(BILLING_UPSELL_PLANS_PAGE_CLICK, {
    featureKey,
    currentPlan,
    newPlan,
    currentPlanValue: <string>currentPlanValue,
    newPlanValue: <string>newPlanValue,
    source,
  });
};

export const trackingBillingPostUpsellAdbarClick = (origin: string) => {
  logEvent(BILLING_POST_UPSELL_ADBAR_CLICK, { origin });
};

export const trackingBillingSoftBlockPaymentClick = ({
  featureKey = '',
}: Pick<UpsellEventParams, 'featureKey'>) => {
  logEvent(BILLING_SOFT_BLOCK_PAYMENT_CLICK, {
    featureKey,
  });
};

export const trackingBillingHardBlockPaymentClick = ({
  featureKey = '',
}: Pick<UpsellEventParams, 'featureKey'>) => {
  logEvent(BILLING_HARD_BLOCK_PAYMENT_CLICK, {
    featureKey,
  });
};

export const trackingBillingSoftBlockModalShow = ({
  featureKey = '',
}: Pick<UpsellEventParams, 'featureKey'>) => {
  logEvent(BILLING_SOFT_BLOCK_MODAL_SHOW, {
    featureKey,
  });
};

export const trackingBillingHardBlockModalShow = ({
  featureKey = '',
}: Pick<UpsellEventParams, 'featureKey'>) => {
  logEvent(BILLING_HARD_BLOCK_MODAL_SHOW, {
    featureKey,
  });
};

export const trackingBillingTestDiscountsAdBarShow = () => {
  logEvent(TEST_DISCOUNTS_ADBAR_SHOW, {});
};

export const trackingTestDiscounysAdBarClick = () => {
  logEvent(TEST_DISCOUNTS_ADBAR_CLICK, {});
};

export const trackingChurnStatusCheck = ({
  status,
  route,
}: ChurnStatusCheckParams) => {
  logEvent(BILLING_CHURN_STATUS_CHECK, { status, route });
};

export const trackingBillingUpsellDiscountShow = () => {
  logEvent(BILLING_UPSELL_DISCOUNT_SHOW, {});
};

export const trackingBillingUpsellDiscountClose = () => {
  logEvent(BILLING_UPSELL_DISCOUNT_CLOSE, {});
};

export const trackingBillingAdbarDiscountUpgradeShow = () => {
  logEvent(BILLING_ADBAR_DISCOUNT_UPGRADE_SHOW, {});
};

export const trackingBillingAdbarUpsellDiscountClick = () => {
  logEvent(BILLING_ADBAR_DISCOUNT_UPGRADE_CLICK, {});
};

export const trackingBillingUpsellDiscountClick = () => {
  logEvent(BILLING_UPSELL_DISCOUNT_CLICK, {});
};

export const trackingBillingCancelScheduledDowngradeClick = (
  origin: string,
) => {
  logEvent(BILLING_CANCEL_SCHEDULED_DOWNGRADE_CLICK, { origin });
};

export const trackingBillingCancelScheduledDowngradeConfirm = (
  origin: string,
) => {
  logEvent(BILLING_CANCEL_SCHEDULED_DOWNGRADE_CONFIRM, { origin });
};
